import { useContext, useMemo } from "react";
import cx from "classnames";
import { Icon } from "@foris/avocado-icons";
import {
  Button,
  CardNotification,
  Loading,
  RoundButton,
  Table,
  type TableColumn,
  Tooltip,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { ContextApp, type IContextApp } from "@config/Context/contextApp";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import type { IParams } from "@models/IParams";
import type { Link, Package } from "@models/ISchema";
import { LinkSelector } from "@modules/shared/selectors/Selectors";
import {
  getColumnCell,
  getHeadCell,
  getLinkCell,
  getMoreOrLessPillCell,
} from "@common/utils/table.util";
import useAssignLinks, { type ErrorMessagesByType } from "../../hooks/useAssignLinks";
import useLinksState from "../../hooks/useLinksState";
import LinkAssignmentErrors, { isErrorMessage } from "../LinkAssignmentErrors";
import css from "./packageLeaguesSummary.module.scss";
import GroupTableCourseColumn from "@modules/vacancies/Groups/group-table-course-column";

interface PackageLeaguesSummaryProps {
  isLoading: boolean;
  packageData: Package | null;
  reloadPackageData: () => void | Promise<void>;
}

const getLeftActionColumn = (
  id: string,
  linksToAdd: Record<string, Link> = {},
  linksToDelete: Record<string, Link> = {},
  errorMessagesByType: ErrorMessagesByType[] = [],
) => {
  const hasError = errorMessagesByType.some(error => Object.keys(error.links).includes(id));

  if (hasError) {
    return (
      <Icon
        className={css.packageLeaguesSummary_statusIcon}
        name="alert-triangle"
        size="sm"
        color="neutral-70"
      />
    );
  }

  if (id in linksToDelete) {
    return (
      <Icon
        className={cx(css.packageLeaguesSummary_statusIcon, css.packageLeaguesSummary_trashIcon)}
        name="trash"
        size="sm"
        color="neutral-70"
      />
    );
  }

  if (id in linksToAdd) {
    return (
      <Icon
        className={cx(css.packageLeaguesSummary_statusIcon, css.packageLeaguesSummary_addIcon)}
        filled
        name="circle-plus"
        size="sm"
        color="neutral-70"
      />
    );
  }

  return null;
};

const PackageLeaguesSummary = ({
  isLoading,
  packageData,
  reloadPackageData,
}: PackageLeaguesSummaryProps) => {
  const { id, origin, scenario, workspace }: IParams = useParams();
  const history = useHistory();
  const context: IContextApp = useContext(ContextApp);
  const { editorUrl, editAssignmentUrl } = useNavigationUrl();
  const { t, i18n } = useTranslation();
  const {
    linksToAdd,
    linksToDelete,
    assignmentIds,
    deletionsIds,
    assignmentsWithMultipleGroups,
    handleResetChanges,
    setLinksToAdd,
    setLinksToDelete,
  } = useLinksState({
    packageId: id,
  });

  const {
    handleLinkAssignment,
    isLoading: isLoadingChanges,
    errorMessagesByType,
  } = useAssignLinks({
    scenarioId: scenario,
    originId: origin,
    assignments: assignmentIds,
    deletions: deletionsIds,
    onSavedChanges: () => {
      void reloadPackageData();
      handleResetChanges();
    },
  });

  const canEditAssignments = Boolean(
    context?.user?.permissions?.update && context?.user?.abilities?.can_edit_assignment,
  );
  const isEditionEnabled = canEditAssignments && !packageData?.isPublished;

  const tableColumns = useMemo(
    () =>
      [
        {
          header: getHeadCell(t("search-results.dimension.link.table.header.code")),
          renderCell: ({ code, id: linkId }: Link) => (
            <div className={css.packageLeaguesSummary_cellWithIcon}>
              {getLeftActionColumn(linkId, linksToAdd, linksToDelete, errorMessagesByType)}
              {getLinkCell(editorUrl("link", linkId), null, code)}
            </div>
          ),
          desktopOptions: { headCellStyles: { minWidth: "250px" } },
        },
        {
          header: getHeadCell(t("search-results.dimension.link.table.header.course-name")),
          renderCell: ({ course, bundle }: Link) => {
            const props = {
              groupLabel: `${course?.code} - ${course?.name}`,
              termCode: bundle?.term?.code,
              campusCode: course?.curriculum?.program?.campus?.code,
              departmentCode: course?.curriculum?.program?.department?.code,
              modalityCode: course?.curriculum?.program?.modality?.code,
              shiftCode: course?.curriculum?.program?.shift?.code,
              programCode: course?.curriculum?.program?.code,
              curriculumCode: course?.curriculum?.code,
            };

            return <GroupTableCourseColumn {...props} />;
          },
          desktopOptions: {
            headCellStyles: {
              minWidth: "80px",
            },
          },
        },
        {
          header: getHeadCell(t("package-link-management.table.header.groups")),
          renderCell: ({ groups }: Link) => getMoreOrLessPillCell(groups),
          desktopOptions: { headCellStyles: { minWidth: "80px", width: "250px" } },
        },
        {
          header: getHeadCell(t("search-results.dimension.link.table.header.vacancies")),
          renderCell: ({ vacancies }: Link) => getColumnCell(vacancies ?? "-"),
          desktopOptions: {
            headCellStyles: {
              minWidth: "80px",
              width: "124px",
            },
          },
        },
        {
          header: getHeadCell(t("package-wizard.summary.actions")),
          renderCell: ({ id: linkId }: Link) => (
            <Button variant="ghost" onClick={() => history.push(editAssignmentUrl(linkId))}>
              {t("package-wizard.summary.edit-schedule")}
            </Button>
          ),
          desktopOptions: {
            headCellStyles: {
              minWidth: "140px",
            },
          },
        },
      ] as TableColumn[],
    [i18n.language, linksToAdd, linksToDelete, errorMessagesByType, history, editAssignmentUrl],
  );

  const hasErrors = useMemo(
    () =>
      errorMessagesByType.some(error => isErrorMessage(error.type, error.userCanSkipValidations)),
    [errorMessagesByType],
  );

  const linksToAvoidInSelector = useMemo(
    () => packageData?.links?.map(link => link.id) || [],
    [packageData?.links],
  );
  const packageLinks = useMemo(
    () => [...(packageData?.links || []), ...Object.values(linksToAdd)],
    [packageData?.links, linksToAdd],
  );

  if (isLoading || !packageData) return <Loading />;

  const handleDeleteLink = (link: Link) => {
    if (link.id in linksToDelete) {
      const newLinksToDelete = { ...linksToDelete };
      delete newLinksToDelete[link.id];
      setLinksToDelete(newLinksToDelete);
      return;
    }

    if (link.id in linksToAdd) {
      const newLinksToAdd = { ...linksToAdd };
      delete newLinksToAdd[link.id];
      setLinksToAdd(newLinksToAdd);
      return;
    }

    setLinksToDelete({ ...linksToDelete, [link.id]: link });
  };

  const handleSelectLink = (link: Link) => {
    if (!link?.id || linksToAdd[link.id]) return;

    void handleLinkAssignment(true, true, [
      ...assignmentIds,
      {
        linkId: link.id,
        packageId: id,
      },
    ]);
    setLinksToAdd({ ...linksToAdd, [link.id]: link });
  };

  return (
    <section className={css.packageLeaguesSummary}>
      {isLoadingChanges && <Loading />}

      {isEditionEnabled && (
        <LinkSelector
          key={i18n.language}
          className={css.packageLeaguesSummary_linkSelector}
          placeholder={t("package-link-management.selector-placeholder")}
          label=""
          onSelectLink={handleSelectLink}
          linksToAvoid={linksToAvoidInSelector}
          keepSelection={false}
          selectedExternalOptions={linksToAdd}
          splitByGroups
          campusId={packageData.population?.campus?.id}
          onlyCoursesGeneratingPackages
        />
      )}

      {assignmentsWithMultipleGroups > 0 && (
        <CardNotification
          className={css.infoCard}
          state="info"
          title={t("package-link-management.info-title", {
            letter: assignmentsWithMultipleGroups > 1 ? "s" : "",
          })}
          outlined
        >
          {assignmentsWithMultipleGroups > 1
            ? t("package-link-management.plural-info-message")
            : t("package-link-management.singular-info-message")}
        </CardNotification>
      )}

      <Table
        columns={tableColumns}
        data={packageLinks}
        rightActions={
          isEditionEnabled
            ? [
                {
                  renderContent: (link: unknown) => (
                    <Tooltip
                      label={t("package-link-management.table.actions.delete-tooltip")}
                      placement="left"
                    >
                      <RoundButton
                        variant="ghost"
                        size="sm"
                        icon="circle-minus"
                        onClick={() => handleDeleteLink(link as Link)}
                      />
                    </Tooltip>
                  ),
                },
              ]
            : undefined
        }
      />

      <LinkAssignmentErrors
        origin={origin}
        scenario={scenario}
        workspace={workspace}
        errorMessagesByType={errorMessagesByType}
      />

      <div className={css.packageLeaguesSummary_actions}>
        <Button disabled={isLoadingChanges} variant="ghost" onClick={handleResetChanges}>
          {isEditionEnabled
            ? t("package-link-management.btn-cancel")
            : t("package-link-management.btn-back")}
        </Button>

        {isEditionEnabled && (
          <Button
            disabled={
              isLoadingChanges || (!assignmentIds.length && !deletionsIds.length) || hasErrors
            }
            onClick={() => void handleLinkAssignment(false, false)}
          >
            {t("package-link-management.btn-save")}
          </Button>
        )}
      </div>
    </section>
  );
};

export default PackageLeaguesSummary;
